import { useQueryClient } from '@tanstack/react-query';
import { useResolveRoute } from '@mews/routing';
import { RouteName, ActionType, ArticlePath, createGuideLink, TextKey } from '@mews-commander/enums';
import { LocalizedProperty, LocalizedText, lt, showSuccessAlert, useLocalizeProperty, useUUID } from '@mews/framework';
import {
    ContentContainer,
    Loader,
    ModalWrapper,
    NewModalBodyWrapper,
    SmallContentContainer,
    WithBreakpointRange,
    isMobileOrTablet,
} from '@mews-commander/ui';
import { IconName, StackItem, withBreakpointRange } from '@optimus-web/core';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { FormLanguageProvider } from '@mews-commander/legacy-form';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { Breadcrumbs } from '../../components/BreadcrumbList';
import ModalHeader from '../../legacy/React/Components/Common/ModalHeader';
import { useServicesGetAllQuery } from '../services';
import { LinkMenu, ListOption } from '../../components';
import { navigateInModal, showRouteInModal } from '../app/actions/common';
import { MenuViewWrapper, RightBodyWrapper } from '../enterpriseBuilder/components/style';
import { HeadingAction } from '../../legacy/React/Components/HeadingAction/Action/types';
import { getRateGroupsGetAllQueryKey, useRateGroupsGetAllQuery } from './useRateGroupsGetAllQuery';
import { RateGroupEditForm } from './RateGroupEditForm';
import { useRateGroupsDeleteMutation } from './useRateGroupsDeleteMutation';

interface RateGroupDetailViewProps extends WithBreakpointRange {
    rateGroupId: string;
}

const BaseRateGroupDetailView = ({ rateGroupId, breakpointRange }: RateGroupDetailViewProps) => {
    const formScope = `rate-group-create-${useUUID()}`;

    const dispatch = useCommanderThunkDispatch();
    const resolveRoute = useResolveRoute();
    const currentEnterpriseId = useCurrentEnterpriseId();
    const { name: enterpriseName } = useCurrentEnterprise();

    const queryClient = useQueryClient();
    const { mutate } = useRateGroupsDeleteMutation();

    const lp = useLocalizeProperty();

    const queryParams = {
        enterpriseIds: [currentEnterpriseId],
        rateGroupIds: [rateGroupId],
    };
    const { isLoading: isRateGroupsLoading, data: rateGroupsData } = useRateGroupsGetAllQuery(queryParams);

    const rateGroup = rateGroupsData?.rateGroups[0];
    const serviceId = rateGroup?.serviceId;

    const { isLoading: isServicesLoading, data: service } = useServicesGetAllQuery(
        {
            enterpriseIds: [currentEnterpriseId],
            serviceIds: [serviceId as string],
        },
        {
            select: (data) => data.services.find((s) => s.id === serviceId),
            enabled: !!serviceId,
        }
    );

    const isLoading = isRateGroupsLoading || isServicesLoading;

    if (isLoading) {
        return (
            <ModalWrapper>
                <SmallContentContainer>
                    <Loader />
                </SmallContentContainer>
            </ModalWrapper>
        );
    }

    if (rateGroup && service && serviceId) {
        const breadcrumbs: Breadcrumbs = [
            {
                text: enterpriseName,
                url: resolveRoute(RouteName.EnterpriseDetail, { enterpriseId: currentEnterpriseId }),
            },
            {
                text: lt('ServicePlural'),
                url: resolveRoute(RouteName.ServiceIndex, { query: { OwnerId: currentEnterpriseId } }),
            },
            {
                text: <LocalizedProperty property={service?.names} />,
                url: resolveRoute(RouteName.VisitDetail, { serviceId }),
            },
            {
                text: lt('RateGroupPlural'),
                url: resolveRoute(RouteName.RateGroupIndex, { query: { OwnerId: serviceId } }),
            },
            {
                text: <LocalizedProperty property={rateGroup.names} />,
                active: true,
            },
        ];

        const rateGroupName = lp(rateGroup.names);
        const actions = [
            {
                icon: IconName.Delete,
                actionId: 'delete',
                text: lt('DeleteHint'),
                title: <LocalizedText textKey={TextKey.DeleteConfirmation} substitutes={{ entity: rateGroupName }} />,
                buttonText: lt('Delete'),
                isDanger: true,
                onConfirmation: () => {
                    mutate(
                        { enterpriseIds: [currentEnterpriseId], rateGroupIds: [rateGroupId] },
                        {
                            onSuccess: () => {
                                showSuccessAlert({
                                    description: lt('DeleteSuccessMessage', { entity: rateGroupName }),
                                });
                                dispatch(showRouteInModal(RouteName.RateGroupIndex, { query: { OwnerId: serviceId } }));
                                void queryClient.invalidateQueries({
                                    queryKey: getRateGroupsGetAllQueryKey(queryParams),
                                });
                            },
                        }
                    );
                },
                type: ActionType.ConfirmationDialog,
            },
            {
                url: createGuideLink(ArticlePath.CreateModifyDeleteRateGroup),
                target: '_blank',
                icon: IconName.Help,
                text: lt('Help'),
                type: ActionType.Link,
            },
        ] satisfies HeadingAction[];

        const menuOptions = [
            {
                primaryText: lt('SettlementRulePlural'),
                value: resolveRoute(RouteName.SettlementRuleIndex, { query: { OwnerId: rateGroupId } }),
            },
            {
                primaryText: lt('CancellationPolicyPlural'),
                value: resolveRoute(RouteName.CancellationPolicyIndex, { query: { OwnerId: rateGroupId } }),
            },
        ] satisfies ListOption[];

        const stacked = isMobileOrTablet(breakpointRange);

        const ContentContainerComponent = stacked ? ContentContainer : SmallContentContainer;

        return (
            <ModalWrapper>
                <ModalHeader breadcrumbs={breadcrumbs} actions={actions} />
                <NewModalBodyWrapper>
                    <MenuViewWrapper stacked={stacked}>
                        <StackItem>
                            <LinkMenu
                                options={menuOptions}
                                selected={null}
                                nameKey={TextKey.Settings}
                                handleNavigation={(url) => dispatch(navigateInModal(url))}
                            />
                        </StackItem>
                        <RightBodyWrapper>
                            <StackItem>
                                <ContentContainerComponent>
                                    <FormLanguageProvider>
                                        <RateGroupEditForm
                                            form={formScope}
                                            serviceId={serviceId}
                                            rateGroupId={rateGroupId}
                                        />
                                    </FormLanguageProvider>
                                </ContentContainerComponent>
                            </StackItem>
                        </RightBodyWrapper>
                    </MenuViewWrapper>
                </NewModalBodyWrapper>
            </ModalWrapper>
        );
    }

    return null;
};

export const RateGroupDetailView = withBreakpointRange(BaseRateGroupDetailView);
